import { Injectable, OnDestroy } from '@angular/core';
import {
  HttpClient,
  HttpErrorResponse,
  HttpParams,
} from '@angular/common/http';
import {
  BehaviorSubject,
  from,
  interval,
  Observable,
  of,
  PartialObserver,
  Subscription,
  throwError,
} from 'rxjs';
import { catchError, map, switchMap, take, tap } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { Jwt } from '../../api/benutzermanagement/models';
import { BenutzermanagementAPIService } from '../../api/benutzermanagement/benutzermanagement-api.service';
import { ManagedAccountService } from '../../api/managed-account/managed-account.service';
import { AppToastService } from '../../toast/app-toast.service';
import { getBenutzerInfoFeatureState } from '../../reducers/authinfo.reducer';
import { environment } from '../../../environments/environment';
import { config } from '../module.config';
import {
  mapDtoToNotification,
  MarkAllAsReadResponse,
  Notification,
  NotificationDTO,
  NotificationQueryParams,
  NotificationRequest,
  NotificationStatus,
  PaginatedNotificationResponse,
  PatchedNotificationRequest,
  PatchedUserProfileRequest,
  ShortLinkCreateResponse,
  UnreadCountResponse,
  UserProfile,
  UserProfileRequest,
} from '../models/notification.model';

@Injectable({
  providedIn: 'root',
})
export class NotificationService implements OnDestroy {
  private notificationsSubject = new BehaviorSubject<Notification[]>([]);
  public notifications$ = this.notificationsSubject.asObservable();

  private unreadCountSubject = new BehaviorSubject<number>(0);
  public unreadCount$ = this.unreadCountSubject.asObservable();

  private totalCountSubject = new BehaviorSubject<number>(0);
  public totalCount$ = this.totalCountSubject.asObservable();

  private loadingSubject = new BehaviorSubject<boolean>(false);
  public loading$ = this.loadingSubject.asObservable();

  private jwt: Jwt;
  private savedJwt: Jwt | null = null;
  private savedAccountUuid: string | null = null;
  private hasSavedAccountUuid = false;
  private currentOboUuid: string | null = null;

  private interval: Observable<number>;
  private intervalSubscription: Subscription;
  private managedAccountSubscription: Subscription;
  private pollSubscription: Subscription;
  private benutzerState$: Subscription;

  private baseURL: string = environment.notifications_url || null;
  private authHeaders: { Authorization: string } = {
    Authorization: '-',
  };
  private isAuthenticated = false;

  private jwtUpdateObserver: PartialObserver<number> = {
    complete: () => {},
    error: () => {
      this.toastService.show(
        'Es gibt ein Authentifizierungsproblem',
        'Sie können das Benachrichtigungsmodul momentan nicht benutzen. Bitte versuchen Sie es später noch ein Mal. (V.100)',
        'danger',
      );
    },
    next: () => {
      const activeAccount =
        this.managedAccountService.getCurrentActiveAccount();
      this.benutzerAPI.requestJwt(config.UUID, activeAccount).subscribe(
        (r) => {
          this.jwt = r;
          this.updateAuthHeaders();
        },
        (error: HttpErrorResponse) => {
          if (error.status === 406) {
            // first time usage
            // versuche Aktivierung
            this.benutzerAPI
              .activateProduct(config.UUID, {
                shared_data: [], // no contents required -> internal product
              })
              .subscribe(
                () => {
                  // ok läuft - nun jwt abholen
                  const activeAccountAfterActivation =
                    this.managedAccountService.getCurrentActiveAccount();
                  this.benutzerAPI
                    .requestJwt(config.UUID, activeAccountAfterActivation)
                    .subscribe(
                      (r) => {
                        this.jwt = r;
                        this.updateAuthHeaders();
                      },
                      () => {
                        this.toastService.show(
                          'Es gibt ein Authentifizierungsproblem',
                          'Sie können das Benachrichtigungsmodul momentan nicht benutzen. Bitte versuchen Sie es später noch ein Mal. (V.100)',
                          'danger',
                        );
                      },
                    );
                },
                () => {
                  this.toastService.show(
                    'Es gibt ein Authentifizierungsproblem',
                    'Sie können das Benachrichtigungsmodul momentan nicht benutzen. Bitte versuchen Sie es später noch ein Mal. (V.100)',
                    'danger',
                  );
                },
              );
          }
        },
      );
    },
  };

  constructor(
    private benutzerAPI: BenutzermanagementAPIService,
    private managedAccountService: ManagedAccountService,
    private store: Store,
    private toastService: AppToastService,
    private httpClient: HttpClient,
  ) {
    this.interval = interval(5 /* min */ * 60 /* s */ * 1000 /* ms */);

    this.benutzerState$ = this.store
      .select(getBenutzerInfoFeatureState)
      .subscribe((s) => {
        if (s.products?.allRawProducts?.length) {
          for (const p of s.products.allRawProducts) {
            if (p.uuid === config.UUID) {
              this.baseURL = `${environment.protocol}://${p.host}`;
              break;
            }
          }
        }
        if (!this.baseURL && environment.notifications_url) {
          this.baseURL = environment.notifications_url;
        }

        let active = false;
        if (s.products?.activeProducts?.length) {
          for (const p of s.products.activeProducts) {
            if (p.uuid === config.UUID) {
              active = true;
              break;
            }
          }
        }

        this.isAuthenticated = !!s.auth?.isAuthenticated;
        if ((active || this.baseURL) && this.isAuthenticated) {
          this.ensureJwtUpdateRunning(true);
          this.startPolling();
        } else {
          this.ensureJwtUpdateRunning(false);
          this.stopPolling();
          this.notificationsSubject.next([]);
          this.unreadCountSubject.next(0);
          this.totalCountSubject.next(0);
        }
      });

    this.managedAccountSubscription = this.managedAccountService
      .getActiveAccount()
      .subscribe((uuid) => {
        if (uuid !== this.currentOboUuid) {
          // Wenn der Account manuell gewechselt wurde (nicht über switchToManagedAccount),
          // dann beenden wir einen eventuell aktiven OBO-Kontext und aktualisieren das JWT.
          if (this.currentOboUuid) {
            this.currentOboUuid = null;
            this.hasSavedAccountUuid = false;
            this.savedAccountUuid = null;
            this.savedJwt = null;
          }
          if (this.isAuthenticated) {
            this.jwtUpdateObserver.next(0);
          }
        }
      });
  }

  ngOnDestroy(): void {
    this.benutzerState$?.unsubscribe();
    this.managedAccountSubscription?.unsubscribe();
    this.ensureJwtUpdateRunning(false);
    this.stopPolling();
  }

  public getBaseURL(): string {
    return this.baseURL || environment.notifications_url || '';
  }

  public setBaseURL(url: string): void {
    this.baseURL = url;
  }

  public getAuthHeaders(): { Authorization: string } {
    return { ...this.authHeaders };
  }

  public manuallyStartJwtUpdate(): void {
    this.ensureJwtUpdateRunning(true);
  }

  public isOboActive(): boolean {
    return this.currentOboUuid !== null;
  }

  /**
   * Switch notification service authentication to act on behalf of a specific managed account.
   * This will fetch a product JWT with the provided managed account UUID and apply it
   * to subsequent Notification API calls.
   */
  public switchToManagedAccount(onBehalfOfUuid: string): Observable<boolean> {
    if (this.currentOboUuid === onBehalfOfUuid && this.jwt) {
      return of(true);
    }
    this.currentOboUuid = onBehalfOfUuid;
    return this.benutzerAPI.requestJwt(config.UUID, onBehalfOfUuid).pipe(
      take(1),
      map((jwt) => {
        // Save current JWT to allow restoring after leaving the case context
        if (!this.savedJwt) {
          this.savedJwt = this.jwt ?? null;
        }

        // Save current active account to allow restoring after leaving the case context
        if (!this.hasSavedAccountUuid) {
          this.savedAccountUuid =
            this.managedAccountService.getCurrentActiveAccount();
          this.hasSavedAccountUuid = true;
        }

        // Set the active account for global use (e.g. JWT refreshes)
        this.managedAccountService.setActiveAccount(onBehalfOfUuid);

        this.jwt = jwt;
        this.authHeaders = { Authorization: `JWT ${jwt.encoded_jwt}` };
        this.refreshUnreadCount();
        return true as const;
      }),
    );
  }

  /**
   * Restores the default authentication token after a temporary OBO context.
   * If a previous JWT was saved via switchToManagedAccount, it will be restored.
   * Otherwise, a fresh default product JWT is requested.
   */
  public restoreDefaultToken(): Observable<boolean> {
    const oboToClear = this.currentOboUuid;
    this.currentOboUuid = null;

    let accountWasRestored = false;
    // Restore previously active account if one was saved AND it hasn't been changed manually in the meantime
    if (this.hasSavedAccountUuid) {
      if (this.managedAccountService.getCurrentActiveAccount() === oboToClear) {
        if (this.savedAccountUuid) {
          this.managedAccountService.setActiveAccount(this.savedAccountUuid);
        } else {
          this.managedAccountService.clearActiveAccount();
        }
        accountWasRestored = true;
      }
      this.hasSavedAccountUuid = false;
      this.savedAccountUuid = null;
    }

    if (this.savedJwt && accountWasRestored) {
      this.jwt = this.savedJwt;
      this.savedJwt = null;
      this.updateAuthHeaders();
      this.refreshUnreadCount();
      return of(true);
    }

    this.savedJwt = null;
    const activeAccount = this.managedAccountService.getCurrentActiveAccount();
    return this.benutzerAPI.requestJwt(config.UUID, activeAccount).pipe(
      take(1),
      map((jwt) => {
        this.jwt = jwt;
        this.updateAuthHeaders();
        this.refreshUnreadCount();
        return true as const;
      }),
    );
  }

  private ensureJwtUpdateRunning(shouldRun: boolean): void {
    if (shouldRun && !this.intervalSubscription) {
      this.intervalSubscription = this.interval.subscribe(
        this.jwtUpdateObserver,
      );
      this.jwtUpdateObserver.next(0);
    } else if (shouldRun && this.intervalSubscription) {
      this.jwtUpdateObserver.next(0);
    } else if (!shouldRun && this.intervalSubscription) {
      this.intervalSubscription.unsubscribe();
      this.intervalSubscription = null;
    }
  }

  private updateAuthHeaders(): void {
    if (this.jwt?.encoded_jwt) {
      this.authHeaders = {
        Authorization: `JWT ${this.jwt.encoded_jwt}`,
      };
    }
  }

  private ensureBaseUrlAndAuthSetAction(
    resolve: (value?: unknown) => void,
    reject: (reason?: unknown) => void,
    counter = 0,
  ): void {
    if (
      this.baseURL !== null &&
      this.authHeaders.Authorization &&
      this.authHeaders.Authorization.startsWith('JWT')
    ) {
      return resolve();
    }
    if (++counter >= 40) {
      // ~ 20 seconds
      return reject();
    }
    setTimeout(
      this.ensureBaseUrlAndAuthSetAction.bind(this),
      500,
      resolve,
      reject,
      counter,
    );
  }

  private ensureBaseUrlSet(): Promise<unknown> {
    return new Promise(this.ensureBaseUrlAndAuthSetAction.bind(this));
  }

  public isReady(): boolean {
    return (
      this.baseURL !== null &&
      !!this.authHeaders.Authorization &&
      this.authHeaders.Authorization.startsWith('JWT')
    );
  }

  private withBaseUrl<T>(a: () => Observable<T>): Observable<T> {
    if (this.isReady()) {
      return a();
    }
    return from(this.ensureBaseUrlSet()).pipe(
      switchMap(() => a()),
      take(1),
    );
  }

  private startPolling(): void {
    this.stopPolling();
    // Poll unread count every 60 seconds
    this.pollSubscription = interval(60000).subscribe(() => {
      this.refreshUnreadCount();
    });
  }

  private stopPolling(): void {
    if (this.pollSubscription) {
      this.pollSubscription.unsubscribe();
      this.pollSubscription = null;
    }
  }

  /**
   * Fetches paginated list of notifications with optional filters
   */
  public getNotifications(
    params?: NotificationQueryParams,
  ): Observable<PaginatedNotificationResponse> {
    return this.withBaseUrl<PaginatedNotificationResponse>(() => {
      this.loadingSubject.next(true);

      let httpParams = new HttpParams();
      if (params) {
        if (params.page !== undefined) {
          httpParams = httpParams.set('page', params.page.toString());
        }
        if (params.page_size !== undefined) {
          httpParams = httpParams.set('page_size', params.page_size.toString());
        }
        if (params.limit !== undefined) {
          httpParams = httpParams.set('limit', params.limit.toString());
        }
        if (params.offset !== undefined) {
          httpParams = httpParams.set('offset', params.offset.toString());
        }
        if (params.status) {
          httpParams = httpParams.set('status', params.status);
        }
        if (params.priority) {
          httpParams = httpParams.set('priority', params.priority);
        }
        if (params.message_type) {
          httpParams = httpParams.set('message_type', params.message_type);
        }
        if (params.channel) {
          httpParams = httpParams.set('channel', params.channel);
        }
        if (params.search) {
          httpParams = httpParams.set('search', params.search);
        }
        if (params.ordering) {
          httpParams = httpParams.set('ordering', params.ordering);
        }
      }

      const url = `${this.getBaseURL()}/api/notifications/`;
      return this.httpClient
        .get<PaginatedNotificationResponse>(url, {
          headers: { ...this.authHeaders },
          params: httpParams,
        })
        .pipe(
          tap((response) => {
            const notifications = (response.results || []).map(
              mapDtoToNotification,
            );
            this.notificationsSubject.next(notifications);
            this.totalCountSubject.next(response.count || 0);
            this.loadingSubject.next(false);
          }),
          catchError((err: unknown) => {
            this.loadingSubject.next(false);
            return throwError(() => err);
          }),
        );
    });
  }

  /**
   * Convenience method to load notifications and return mapped Notification items
   */
  public loadNotifications(
    params?: NotificationQueryParams,
  ): Observable<Notification[]> {
    return this.getNotifications(params).pipe(
      map((response) => (response.results || []).map(mapDtoToNotification)),
    );
  }

  /**
   * Fetches a single notification by its ID
   */
  getNotification(id: string): Observable<Notification> {
    return this.withBaseUrl<Notification>(() => {
      const url = `${this.getBaseURL()}/api/notifications/${id}/`;
      return this.httpClient
        .get<NotificationDTO>(url, {
          headers: { ...this.authHeaders },
        })
        .pipe(
          map(mapDtoToNotification),
          tap((notification) => {
            this.updateNotificationInState(notification);
          }),
        );
    });
  }

  /**
   * Full update of a notification (PUT)
   */
  updateNotification(
    id: string,
    data: NotificationRequest | PatchedNotificationRequest,
  ): Observable<Notification> {
    return this.withBaseUrl<Notification>(() => {
      const url = `${this.getBaseURL()}/api/notifications/${id}/`;
      return this.httpClient
        .put<NotificationDTO>(url, data, {
          headers: { ...this.authHeaders },
        })
        .pipe(
          map(mapDtoToNotification),
          tap((notification) => {
            this.updateNotificationInState(notification);
          }),
        );
    });
  }

  /**
   * Partial update of a notification (PATCH)
   */
  patchNotification(
    id: string,
    data: PatchedNotificationRequest,
  ): Observable<Notification> {
    return this.withBaseUrl<Notification>(() => {
      const url = `${this.getBaseURL()}/api/notifications/${id}/`;
      return this.httpClient
        .patch<NotificationDTO>(url, data, {
          headers: { ...this.authHeaders },
        })
        .pipe(
          map(mapDtoToNotification),
          tap((notification) => {
            this.updateNotificationInState(notification);
          }),
        );
    });
  }

  /**
   * Marks a notification as read
   */
  markAsRead(id: string): Observable<Notification> {
    const readAt = new Date().toISOString();
    return this.patchNotification(id, {
      status: NotificationStatus.READ,
      read_at: readAt,
    }).pipe(
      tap(() => {
        const count = this.unreadCountSubject.value;
        if (count > 0) {
          this.unreadCountSubject.next(count - 1);
        }
      }),
    );
  }

  /**
   * Archives a notification
   */
  archive(id: string): Observable<Notification> {
    return this.patchNotification(id, {
      status: NotificationStatus.ARCHIVED,
    }).pipe(
      tap(() => {
        const current = this.notificationsSubject.value;
        const index = current.findIndex((n) => n.id === id);
        if (index > -1 && current[index].status === NotificationStatus.UNREAD) {
          const count = this.unreadCountSubject.value;
          if (count > 0) {
            this.unreadCountSubject.next(count - 1);
          }
        }
      }),
    );
  }

  /**
   * Sets a reminder for a notification
   */
  setReminder(id: string, reminderAt: Date): Observable<Notification> {
    return this.patchNotification(id, {
      reminder_at: reminderAt.toISOString(),
    }).pipe(
      tap(() => {
        const current = this.notificationsSubject.value;
        const index = current.findIndex((n) => n.id === id);
        if (index > -1) {
          const updatedList = [...current];
          updatedList[index] = {
            ...updatedList[index],
            reminderAt,
          };
          this.notificationsSubject.next(updatedList);
        }
      }),
    );
  }

  /**
   * Marks all notifications as read
   */
  markAllAsRead(): Observable<MarkAllAsReadResponse> {
    return this.withBaseUrl<MarkAllAsReadResponse>(() => {
      const url = `${this.getBaseURL()}/api/notifications/mark-all-as-read/`;
      return this.httpClient
        .post<MarkAllAsReadResponse>(
          url,
          {},
          {
            headers: { ...this.authHeaders },
          },
        )
        .pipe(
          tap(() => {
            this.unreadCountSubject.next(0);
            const updated = this.notificationsSubject.value.map((n) => ({
              ...n,
              isRead: true,
              readAt: n.readAt || new Date(),
            }));
            this.notificationsSubject.next(updated);
          }),
        );
    });
  }

  /**
   * Fetches unread notification count
   */
  getUnreadCount(): Observable<UnreadCountResponse> {
    return this.withBaseUrl<UnreadCountResponse>(() => {
      const url = `${this.getBaseURL()}/api/notifications/unread-count/`;
      return this.httpClient
        .get<UnreadCountResponse | { count?: number } | number>(url, {
          headers: { ...this.authHeaders },
        })
        .pipe(
          map((res: UnreadCountResponse | { count?: number } | number) => {
            if (typeof res === 'number') {
              return { unread_count: res };
            }
            if (res && typeof res === 'object') {
              if (
                'unread_count' in res &&
                typeof res.unread_count === 'number'
              ) {
                return { unread_count: res.unread_count };
              }
              if ('count' in res && typeof res.count === 'number') {
                return { unread_count: res.count };
              }
            }
            return { unread_count: 0 };
          }),
          tap((response: UnreadCountResponse) => {
            this.unreadCountSubject.next(response.unread_count);
          }),
          catchError((error: unknown) => {
            return throwError(() => error);
          }),
        );
    });
  }

  public refreshUnreadCount(): void {
    if (!this.isAuthenticated || !this.jwt) {
      return;
    }
    this.getUnreadCount().subscribe({
      next: () => {},
      error: () => {},
    });
  }

  /**
   * User Profile endpoints
   */
  getProfile(): Observable<UserProfile> {
    return this.withBaseUrl<UserProfile>(() => {
      const url = `${this.getBaseURL()}/api/profile/`;
      return this.httpClient.get<UserProfile>(url, {
        headers: { ...this.authHeaders },
      });
    });
  }

  updateProfile(profile: UserProfileRequest): Observable<UserProfile> {
    return this.withBaseUrl<UserProfile>(() => {
      const url = `${this.getBaseURL()}/api/profile/`;
      return this.httpClient.put<UserProfile>(url, profile, {
        headers: { ...this.authHeaders },
      });
    });
  }

  patchProfile(profile: PatchedUserProfileRequest): Observable<UserProfile> {
    return this.withBaseUrl<UserProfile>(() => {
      const url = `${this.getBaseURL()}/api/profile/`;
      return this.httpClient.patch<UserProfile>(url, profile, {
        headers: { ...this.authHeaders },
      });
    });
  }

  /**
   * Link Shortener endpoints
   */
  createShortLink(targetUrl: string): Observable<ShortLinkCreateResponse> {
    return this.withBaseUrl<ShortLinkCreateResponse>(() => {
      const url = `${this.getBaseURL()}/link/`;
      return this.httpClient.post<ShortLinkCreateResponse>(
        url,
        { target_url: targetUrl },
        { headers: { ...this.authHeaders } },
      );
    });
  }

  resolveShortLink(): Observable<any> {
    return this.withBaseUrl<any>(() => {
      const url = `${this.getBaseURL()}/link/resolve/`;
      return this.httpClient.get(url, {
        headers: { ...this.authHeaders },
      });
    });
  }

  updateShortLink(
    shortLink: string,
    targetUrl: string,
  ): Observable<{ short_link: string; target_url: string }> {
    return this.withBaseUrl<{ short_link: string; target_url: string }>(() => {
      const url = `${this.getBaseURL()}/link/update_short_link/`;
      return this.httpClient.post<{ short_link: string; target_url: string }>(
        url,
        { short_link: shortLink, target_url: targetUrl },
        { headers: { ...this.authHeaders } },
      );
    });
  }

  /**
   * Synchronous helper to get cached notification by ID
   */
  getById(id: string): Notification | undefined {
    return this.notificationsSubject.value.find((n) => n.id === id);
  }

  private updateNotificationInState(notification: Notification): void {
    const current = this.notificationsSubject.value;
    const index = current.findIndex((n) => n.id === notification.id);
    if (index > -1) {
      const updatedList = [...current];
      updatedList[index] = notification;
      this.notificationsSubject.next(updatedList);
    }
  }
}
